import { useCallback, useEffect, useRef, useState } from 'react'
import type { NotificationEventEnvelope, UserNotification } from '../api/contracts'
import { markNotificationRead } from '../api/notifications/feed'
import { resolveNotificationPath } from '../components/notifications/notificationUrl'

/** Decision 7.5: a stack of up to three, a new toast pushes out the oldest. */
export const NOTIFICATION_TOASTS_LIMIT = 3
/** The SSE signal can come before the row is committed: «Открыть» and «Прочитано» try once more after this delay. */
export const NOTIFICATION_TOAST_READ_RETRY_MS = 1000

export interface NotificationToast {
  notificationId: number
  eventType: string
  title: string
  /** True while «Открыть» or «Прочитано» waits for the API; the toast does not close by timer then. */
  pending: boolean
}

interface UseNotificationToastsOptions {
  apiBaseUrl: string
  enabled: boolean
  onNavigate: (path: string) => void
  /** Local decrement of the bell count after a toast's notification is marked read. */
  onRead: () => void
  /**
   * Refetch of the bell count when the toast's notification could not be marked read, or when its toast was closed
   * from outside (the feed, «Прочитать все», ✕) while the request ran: the count may already be lowered then.
   */
  onReadFailed: () => void
}

export interface NotificationToastsState {
  toasts: NotificationToast[]
  /** Adds a toast for a `notification_created` signal; ignored while the tab is hidden. */
  show: (event: NotificationEventEnvelope) => void
  open: (notificationId: number) => void
  markRead: (notificationId: number) => void
  close: (notificationId: number) => void
  closeAll: () => void
}

type ReadAttempt = UserNotification | null | 'failed'

export function useNotificationToasts({
  apiBaseUrl,
  enabled,
  onNavigate,
  onRead,
  onReadFailed
}: UseNotificationToastsOptions): NotificationToastsState {
  const [toasts, setToasts] = useState<NotificationToast[]>([])
  const toastsRef = useRef<NotificationToast[]>([])
  const retryTimersRef = useRef(new Set<ReturnType<typeof setTimeout>>())
  const isActiveRef = useRef(true)
  const enabledRef = useRef(enabled)
  /** Toasts closed by someone else while their «Открыть» or «Прочитано» waited for the API. */
  const closedWhilePendingRef = useRef(new Set<number>())
  const callbacksRef = useRef({ onNavigate, onRead, onReadFailed })

  useEffect(() => {
    callbacksRef.current = { onNavigate, onRead, onReadFailed }
  }, [onNavigate, onRead, onReadFailed])

  useEffect(() => {
    enabledRef.current = enabled
  }, [enabled])

  const updateToasts = useCallback((update: (current: NotificationToast[]) => NotificationToast[]) => {
    toastsRef.current = update(toastsRef.current)
    setToasts(toastsRef.current)
  }, [])

  useEffect(() => {
    isActiveRef.current = true
    const retryTimers = retryTimersRef.current
    return () => {
      isActiveRef.current = false
      retryTimers.forEach((timer) => clearTimeout(timer))
      retryTimers.clear()
    }
  }, [])

  const show = useCallback((event: NotificationEventEnvelope) => {
    if (!enabled || document.visibilityState !== 'visible') {
      return
    }
    const { notificationId, eventType, title } = event.payload
    updateToasts((current) => {
      if (current.some((toast) => toast.notificationId === notificationId)) {
        return current
      }
      return [{ notificationId, eventType, title, pending: false }, ...current].slice(0, NOTIFICATION_TOASTS_LIMIT)
    })
  }, [enabled, updateToasts])

  const remove = useCallback((notificationId: number) => {
    updateToasts((current) => current.filter((toast) => toast.notificationId !== notificationId))
  }, [updateToasts])

  const rememberPending = useCallback((closing: NotificationToast[]) => {
    closing.forEach((toast) => {
      if (toast.pending) {
        closedWhilePendingRef.current.add(toast.notificationId)
      }
    })
  }, [])

  const close = useCallback((notificationId: number) => {
    rememberPending(toastsRef.current.filter((toast) => toast.notificationId === notificationId))
    remove(notificationId)
  }, [rememberPending, remove])

  const closeAll = useCallback(() => {
    rememberPending(toastsRef.current)
    updateToasts(() => [])
  }, [rememberPending, updateToasts])

  // Like «Прочитать все»: a pending «Открыть» that answers after `enabled` comes back neither navigates nor decrements.
  useEffect(() => {
    if (!enabled) {
      closeAll()
    }
  }, [enabled, closeAll])

  const waitForRetry = useCallback(() => new Promise<void>((resolve) => {
    const timer = setTimeout(() => {
      retryTimersRef.current.delete(timer)
      resolve()
    }, NOTIFICATION_TOAST_READ_RETRY_MS)
    retryTimersRef.current.add(timer)
  }), [])

  const markReadWithRetry = useCallback(async (notificationId: number): Promise<ReadAttempt> => {
    try {
      const isClosedElsewhere = (): boolean => closedWhilePendingRef.current.has(notificationId)
      const first = await markNotificationRead(apiBaseUrl, notificationId)
      if (first !== null || !isActiveRef.current || !enabledRef.current || isClosedElsewhere()) {
        return first
      }
      await waitForRetry()
      if (isClosedElsewhere()) {
        return first
      }
      return await markNotificationRead(apiBaseUrl, notificationId)
    } catch {
      return 'failed'
    }
  }, [apiBaseUrl, waitForRetry])

  const handleRead = useCallback(async (notificationId: number, navigateAfterRead: boolean) => {
    const toast = toastsRef.current.find((item) => item.notificationId === notificationId)
    if (!toast || toast.pending) {
      return
    }
    updateToasts((current) => current.map((item) => (
      item.notificationId === notificationId ? { ...item, pending: true } : item
    )))

    const result = await markReadWithRetry(notificationId)
    const closedElsewhere = closedWhilePendingRef.current.delete(notificationId)
    if (!isActiveRef.current || !enabledRef.current) {
      return
    }
    remove(notificationId)
    if (closedElsewhere) {
      // The feed or «Прочитать все» may have lowered the count already: refetch instead of a second decrement.
      callbacksRef.current.onReadFailed()
      return
    }
    if (result === null || result === 'failed') {
      // Not stored (rolled back) or the request failed: the toast goes away quietly and the bell is refetched.
      callbacksRef.current.onReadFailed()
      return
    }
    callbacksRef.current.onRead()
    const path = navigateAfterRead ? resolveNotificationPath(result) : null
    if (path) {
      callbacksRef.current.onNavigate(path)
    }
  }, [markReadWithRetry, remove, updateToasts])

  const open = useCallback((notificationId: number) => {
    void handleRead(notificationId, true)
  }, [handleRead])

  const markRead = useCallback((notificationId: number) => {
    void handleRead(notificationId, false)
  }, [handleRead])

  return { toasts, show, open, markRead, close, closeAll }
}
